import React, { useState, useEffect } from 'react';
import { Bell, Check, CheckCheck, X } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';
import axios from 'axios';

// URL centralizado que funciona em produção mesmo com .env errado
import { getBackendUrl } from '../utils/config';
const API_URL = getBackendUrl();

const NotificacoesDropdown = ({ onNavigateToChat, onNavigateToTab }) => {
  const [notificacoes, setNotificacoes] = useState([]);
  const [countNaoLidas, setCountNaoLidas] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    carregarNotificacoes();
    
    // POLLING: Atualizar a cada 30 segundos (reduzido de 5s para evitar sobrecarga)
    // WebSocket pode ser usado para notificações em tempo real se necessário
    const interval = setInterval(carregarNotificacoes, 30000);
    return () => clearInterval(interval);
  }, []);

  const carregarNotificacoes = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      // Adicionar timeout para evitar requests pendentes
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const [notifResponse, countResponse] = await Promise.all([
        axios.get(`${API_URL}/api/notificacoes`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal
        }),
        axios.get(`${API_URL}/api/notificacoes/nao-lidas/count`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal
        })
      ]);

      clearTimeout(timeoutId);
      setNotificacoes(notifResponse.data);
      setCountNaoLidas(countResponse.data.count);
    } catch (error) {
      if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
        console.error('Erro ao carregar notificações:', error);
      }
    }
  };

  // Função para verificar se é notificação de chat/mensagem
  const isNotificacaoChat = (notif) => {
    const titulo = (notif.titulo || '').toLowerCase();
    const tipo = (notif.tipo || '').toLowerCase();
    const mensagem = (notif.mensagem || '').toLowerCase();
    
    // Verificar múltiplas palavras-chave
    const keywords = ['mensagem', 'chat', 'direta', 'nova mensagem'];
    return keywords.some(keyword => 
      titulo.includes(keyword) || tipo.includes(keyword) || mensagem.includes(keyword)
    );
  };

  // Função para verificar se é notificação de férias
  const isNotificacaoFerias = (notif) => {
    const titulo = (notif.titulo || '').toLowerCase();
    const tipo = (notif.tipo || '').toLowerCase();
    const mensagem = (notif.mensagem || '').toLowerCase();
    
    const keywords = ['férias', 'ferias', 'pedido de férias', 'pedido_ferias', 'mapa de ferias'];
    return keywords.some(keyword => 
      titulo.includes(keyword) || tipo.includes(keyword) || mensagem.includes(keyword)
    );
  };

  // Função para lidar com clique na notificação
  const handleNotificacaoClick = async (notif) => {
    const target = getNavigationTarget(notif);
    console.log('Notificação clicada:', notif.titulo, '| Destino:', target);
    
    setIsOpen(false); // Fechar dropdown
    
    if (target) {
      if (target.type === 'chat' && onNavigateToChat) {
        onNavigateToChat(target.data);
      } else if (target.type === 'tab' && onNavigateToTab) {
        onNavigateToTab(target.tab, { subTab: target.subTab, ...target.extraInfo });
      }
    }
    
    // Marcar como lida se ainda não estiver
    if (!notif.lida) {
      marcarComoLida(notif.id);
    }
  };

  // Função para verificar se é notificação de tarefa
  const isNotificacaoTarefa = (notif) => {
    const tipo = (notif.tipo || '').toLowerCase();
    return tipo.includes('tarefa') || tipo === 'nova_tarefa_fixa' || tipo === 'nova_tarefa_pontual';
  };

  // Função para verificar se é notificação de documento
  const isNotificacaoDocumento = (notif) => {
    const tipo = (notif.tipo || '').toLowerCase();
    const titulo = (notif.titulo || '').toLowerCase();
    return tipo.includes('documento') || titulo.includes('documento');
  };

  // Função para verificar se é notificação de agenda/alerta
  const isNotificacaoAgenda = (notif) => {
    const tipo = (notif.tipo || '').toLowerCase();
    const titulo = (notif.titulo || '').toLowerCase();
    return tipo.includes('agenda') || tipo.includes('alerta') || titulo.includes('agenda') || titulo.includes('alerta');
  };

  // Função para verificar se é notificação de horário
  const isNotificacaoHorario = (notif) => {
    const tipo = (notif.tipo || '').toLowerCase();
    const titulo = (notif.titulo || '').toLowerCase();
    return tipo.includes('horario') || tipo.includes('horário') || 
           titulo.includes('horario') || titulo.includes('horário') ||
           tipo.includes('troca') || titulo.includes('troca');
  };

  // Função para verificar se é notificação de avaliação
  const isNotificacaoAvaliacao = (notif) => {
    const tipo = (notif.tipo || '').toLowerCase();
    const titulo = (notif.titulo || '').toLowerCase();
    return tipo.includes('avalia') || titulo.includes('avalia');
  };

  // Função para determinar o destino da navegação
  const getNavigationTarget = (notif) => {
    if (isNotificacaoChat(notif)) {
      return { type: 'chat', data: {
        modoChat: notif.modo_chat || (notif.tipo === 'mensagem_direta' ? 'direto' : 'canais'),
        canal: notif.canal || 'geral',
        conversaUserId: notif.conversa_user_id || null
      }};
    }
    if (isNotificacaoFerias(notif)) {
      return { type: 'tab', tab: 'gestao-colaboradores', subTab: 'ferias', extraInfo: {
        colaboradorId: notif.colaborador_id || notif.user_id || null,
        colaboradorNome: notif.colaborador_nome || null
      }};
    }
    if (isNotificacaoTarefa(notif)) {
      return { type: 'tab', tab: 'tarefas-turno' };
    }
    if (isNotificacaoDocumento(notif)) {
      return { type: 'tab', tab: 'documentos' };
    }
    if (isNotificacaoAgenda(notif)) {
      return { type: 'tab', tab: 'agenda' };
    }
    if (isNotificacaoHorario(notif)) {
      return { type: 'tab', tab: 'horarios' };
    }
    if (isNotificacaoAvaliacao(notif)) {
      return { type: 'tab', tab: 'gestao-colaboradores', subTab: 'avaliacoes' };
    }
    return null;
  };

  // Função para obter texto do destino
  const getDestinationText = (notif) => {
    const target = getNavigationTarget(notif);
    if (!target) return null;
    
    const destinations = {
      'chat': 'Chat',
      'gestao-colaboradores': target.subTab === 'ferias' ? 'Férias' : target.subTab === 'avaliacoes' ? 'Avaliações' : 'Colaboradores',
      'tarefas-turno': 'Tarefas',
      'documentos': 'Documentos',
      'agenda': 'Agenda',
      'horarios': 'Horários'
    };
    
    return destinations[target.tab] || destinations[target.type] || null;
  };

  const marcarComoLida = async (notificacaoId) => {
    try {
      const token = localStorage.getItem('token');
      
      await axios.put(
        `${API_URL}/api/notificacoes/${notificacaoId}/marcar-lida`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      carregarNotificacoes();
    } catch (error) {
      console.error('Erro ao marcar notificação:', error);
      toast.error('Erro ao marcar notificação');
    }
  };

  const marcarTodasLidas = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      await axios.put(
        `${API_URL}/api/notificacoes/marcar-todas-lidas`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Todas as notificações foram marcadas como lidas');
      carregarNotificacoes();
    } catch (error) {
      console.error('Erro ao marcar todas:', error);
      toast.error('Erro ao marcar notificações');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative">
      <Button 
        variant="ghost" 
        size="icon" 
        className="relative"
        onClick={() => setIsOpen(!isOpen)}
      >
        <Bell className="h-5 w-5" />
        {countNaoLidas > 0 && (
          <Badge 
            className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-red-500 text-white text-xs"
          >
            {countNaoLidas > 9 ? '9+' : countNaoLidas}
          </Badge>
        )}
      </Button>

      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 top-12 z-50 w-96 max-h-[500px] overflow-y-auto shadow-lg">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex justify-between items-center">
                  <CardTitle className="text-base">Notificações</CardTitle>
                  <div className="flex gap-2">
                    {countNaoLidas > 0 && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={marcarTodasLidas}
                        disabled={loading}
                        className="text-xs"
                      >
                        <CheckCheck className="h-3 w-3 mr-1" />
                        Marcar todas lidas
                      </Button>
                    )}
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setIsOpen(false)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                {notificacoes.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">
                    Sem notificações
                  </p>
                ) : (
                  notificacoes.map((notif) => {
                    const destinationText = getDestinationText(notif);
                    return (
                    <div
                      key={notif.id}
                      className={`p-3 rounded-md border ${
                        notif.lida ? 'bg-gray-50' : 'bg-blue-50 border-blue-200'
                      } hover:shadow-sm transition-shadow cursor-pointer hover:bg-blue-100`}
                      onClick={() => handleNotificacaoClick(notif)}
                    >
                      <div className="flex justify-between items-start mb-1">
                        <p className="font-semibold text-sm flex items-center gap-1">
                          {notif.titulo}
                          {destinationText && <span className="text-blue-500 text-xs">→ {destinationText}</span>}
                        </p>
                        {!notif.lida && (
                          <Badge variant="destructive" className="text-xs h-5">
                            Nova
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-gray-700 mb-2">{notif.mensagem}</p>
                      <div className="flex justify-between items-center">
                        <p className="text-xs text-gray-500">
                          {notif.data || notif.data_criacao 
                            ? new Date(notif.data || notif.data_criacao).toLocaleString('pt-PT')
                            : ''}
                        </p>
                        {!notif.lida && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="h-6 text-xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              marcarComoLida(notif.id);
                            }}
                          >
                            <Check className="h-3 w-3 mr-1" />
                            Marcar lida
                          </Button>
                        )}
                      </div>
                    </div>
                    );
                  })
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificacoesDropdown;
